import React, { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import LeftSidebar from "@/pages/home/left-sidebar";

import Header from "@/pages/components/header";
import useAuth, { AuthMode } from "@/context/auth-provider";
import { useGroupProvider } from "@/context/group-provider";

import LoadingIndiator from "@/pages/components/loading-indiator";
import {
  useDataAccessConfigQuery,
  useDataAccessListQuery,
} from "@/hooks/use-data-access";
import { getQueue } from "@/lib/data-access-utils";
import { setAppAlertDialogs } from "@/store/app-alert-dialog/app-alert-dialog-slice";
import AppAlertDialog from "@/pages/components/app-alert-dialog";
import { useUserDataQueries } from "@/hooks/use-login";
import { LocationProvider } from "@/context/location-provider";

import { useIsRestoring } from "@tanstack/react-query";
import ChatbotPopup from "@/pages/components/chatbot-popup/chatbot-popup";
import { useCourtOrderDependentQuery } from "@/hooks/use-court-order-dependents";
import { updateCacheWithCourtOrderDependents } from "@/lib/court-order-utils";
import { HeaderSkeleton } from "@/pages/components/header-skeleton";
import RightSidebar from "@/pages/components/right-sidebar";
import {
  mobileViewSignalUrl,
  closeMobileView,
} from "@/signals/mobile-view-signal";
import { useSignals } from "@preact/signals-react/runtime";
import { queryClient } from "@/hooks/use-query-client";
import { alwaysAccessiblePaths, QueryKeys } from "@/constants";
import type { GlobalFlag } from "@/model/global-flag";
import ScreenRouteNames, {
  globalFlagRoutes,
} from "@/constants/screen-route-names";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { getLegacyNameForPath } from "@/constants/screen-route-names";

import ScrollToTop from "@/pages/components/scroll-to-top/scroll-to-top";
import { usePaperlessEob } from "@/hooks/use-paperlessEob";
import { useUiLabels } from "@/hooks/use-ui-labels";
import { useChatSocketInit } from "@/hooks/use-csr-chat";
import PaperlessEobAlert from "@/pages/components/paperlessEob/GoPaperlessEobAlert";
import type { RootState } from "@/store/store";
import { useNotificationError } from "@/context/error-provider";

const MainLayout: React.FC = () => {
  useSignals(); // Enable signals reactivity in this component

  const dispatch = useDispatch();
  const { groupData } = useGroupProvider();
  const { authData } = useAuth();
  const isRestoring = useIsRestoring();
  const navigate = useNavigate();
  const location = useLocation();

  const globalFlag = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    authData?.user?.username,
  ]);

  const isMember = authData?.mode === AuthMode.MEMBER;
  const isCsrChatEnabled =
    isMember &&
    Boolean(
      (
        (globalFlag as { data: GlobalFlag[] })?.data?.[0] as GlobalFlag & {
          showChat?: boolean;
        }
      )?.showChat,
    );

  useChatSocketInit(isCsrChatEnabled);
  // Use the new hook that handles dependent,globalFlag, and providerNetwork queries properly

  const { demographicsQuery, coverageYearsQuery } = useUserDataQueries({
    groupId: groupData?.group_id || "",
    divisionId: groupData?.division_id || "",
    accessToken: authData?.user?.access_token || "",
    username: authData?.user?.username || "",
  });

  const { paperlessEobStatus, isLoadingStatus: isPaperlessEobLoading } =
    usePaperlessEob(demographicsQuery?.data?.memberId);

  const uiLabelsQuery = useUiLabels();

  const {
    isLoading: isCourtOrderDependentLoading,
    data: courtOrderDependent,
    isFetched: isCourtOrderDependentFetched,
    isSuccess: isCourtOrderDependentSuccess,
  } = useCourtOrderDependentQuery();

  const { data: dataAccessConfig, isFetched: isDataAccessConfigFetched } =
    useDataAccessConfigQuery();

  const { data: dataAccessList, isFetched: isDataAccessListFetched } =
    useDataAccessListQuery(dataAccessConfig!);

  // Route guard — evaluated synchronously during render so <Outlet /> never
  // mounts the forbidden page. The navigate + toast fire in a layout effect
  // (useEffect with no delay) immediately after the blocked render.
  const featureListStatus = useSelector(
    (state: RootState) => state.featureList?.status,
  );
  const byPlan = useSelector(
    (state: RootState) => state.featureList?.byPlan ?? {},
  );
  // Read field by field rather than destructured: `activeCoverage` is only
  // filled once login resolves the member's plans, is absent altogether for a
  // member with none, and comes back from `redux-persist`'s hardSet
  // reconciler however it was last written. Destructuring threw on all three
  // and took the whole layout down with it. The guard below already reads a
  // missing coverage code as "not ready" and blocks nothing.
  const activeCoverage = useSelector(
    (state: RootState) => state.planCoverageInfo?.activeCoverage,
  );
  const claimCoverageCode = activeCoverage?.claimCoverageCode;
  const claimCoverageCodeDesc = activeCoverage?.claimCoverageCodeDesc;
  const { addError } = useNotificationError();

  const currentRoute = location.pathname.split("/")[1];

  const globalFlagData = (
    queryClient.getQueryData([
      QueryKeys.GLOBAL_FLAGS,
      authData?.user?.username,
    ]) as { data: GlobalFlag[] } | undefined
  )?.data?.[0];

  // Compute gate results synchronously — used both to block render and in effect
  const isGuardReady = featureListStatus === "succeeded" && !!claimCoverageCode;
  const isAlwaysAccessible = alwaysAccessiblePaths?.includes(location.pathname);

  const inFeatureList = isGuardReady
    ? (byPlan[claimCoverageCode!] ?? []).some(
        (f) => f.action?.web_route === currentRoute,
      )
    : true; // not ready yet — don't block

  const isManagedByAnyPlan = Object.values(byPlan).some((features) =>
    features.some((f) => f.action?.web_route === currentRoute),
  );

  const inGlobalFlag =
    globalFlagData && !isManagedByAnyPlan
      ? (
          Object.entries(globalFlagRoutes) as [keyof GlobalFlag, string[]][]
        ).some(
          ([flagKey, paths]) =>
            globalFlagData[flagKey] && paths.includes(currentRoute),
        )
      : false;

  const shouldBlock =
    isGuardReady && !inFeatureList && !inGlobalFlag && !isAlwaysAccessible;

  // Tracks the last route for which we already emitted a toast, so React 18
  // StrictMode's double-invoke (mount→unmount→remount) and any extra renders
  // don't fire the same toast twice for the same blocked path.
  const lastBlockedRoute = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldBlock) {
      lastBlockedRoute.current = null;
      return;
    }

    const route = location.pathname.split("/")[1];
    if (lastBlockedRoute.current === route) return;
    lastBlockedRoute.current = route;

    const featureName =
      getLegacyNameForPath(route) ??
      route.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    addError({
      message: `"${featureName}" is not available in your ${claimCoverageCodeDesc} plan.`,
      type: ErrorNotificationType.ERROR,
    });

    navigate(`/${ScreenRouteNames.HOME.path}`, { replace: true });
  }, [shouldBlock]); // eslint-disable-line react-hooks/exhaustive-deps

  if (
    isCourtOrderDependentFetched &&
    isCourtOrderDependentSuccess &&
    courtOrderDependent !== null &&
    demographicsQuery?.data
  ) {
    updateCacheWithCourtOrderDependents(
      courtOrderDependent,
      demographicsQuery?.data?.memberId,
    );
  }

  if (
    isDataAccessConfigFetched &&
    isDataAccessListFetched &&
    dataAccessList &&
    demographicsQuery?.data?.memberId
  ) {
    const queue = getQueue(dataAccessList, demographicsQuery?.data?.memberId);
    dispatch(setAppAlertDialogs(queue));
  }

  //set paperless eob in queue
  // if (!paperlessEobStatus?.data?.isOptedIn && demographicsQuery.data) {
  //   dispatch(
  //     setAppAlertDialogs([
  //       {
  //         module: APP_ALERT_MODULE?.PAPERLESS_EOB,
  //         remindLater: alertDialogStatus?.find(
  //           (alertItem: AppAlertDialogStatus) =>
  //             alertItem?.remindLater &&
  //             alertItem?.module === APP_ALERT_MODULE.PAPERLESS_EOB,
  //         ),
  //       },
  //     ]),
  //   );
  // }

  if (
    demographicsQuery.isLoading ||
    isRestoring ||
    isPaperlessEobLoading ||
    uiLabelsQuery.isLoading ||
    coverageYearsQuery.isLoading
  ) {
    return <LoadingIndiator />;
  }
  if (demographicsQuery.isError) {
    return <div>Error</div>;
  }

  return (
    <SidebarProvider>
      {paperlessEobStatus?.data?.showDisclaimer && <PaperlessEobAlert />}
      <AppAlertDialog />
      <LocationProvider>
        <div className="flex min-h-screen w-full flex-col">
          <ScrollToTop />
          {isCourtOrderDependentLoading ? <HeaderSkeleton /> : <Header />}
          <div className="hide-from-third-app full-width flex-1 overflow-hidden md:grid md:grid-cols-[auto,1fr]">
            <div className="hide-from-third-app">
              <LeftSidebar />
            </div>
            <SidebarInset>{shouldBlock ? null : <Outlet />}</SidebarInset>
          </div>
        </div>
        {(globalFlag as { data: GlobalFlag[] })?.data?.[0]?.showAIChat &&
        !isRestoring ? (
          <ChatbotPopup />
        ) : null}
        <RightSidebar
          title="Mobile App View"
          isOpen={Boolean(mobileViewSignalUrl.value)}
          onClose={() => closeMobileView()}
        >
          {mobileViewSignalUrl.value && (
            <iframe
              key={mobileViewSignalUrl.value}
              src={`${mobileViewSignalUrl.value}`}
              className="size-full"
            />
          )}
        </RightSidebar>
      </LocationProvider>
    </SidebarProvider>
  );
};

export default MainLayout;
